import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs } from "react-router-dom";
import { apiClient, PropertyTableLoaderResult } from "~/services/apiClient";
import { LoaderData } from "~/types";
import { loadCookiesData, getCachedAuthAccessInfo } from "~/utils";
import {
  createClientFilteredShouldRevalidate,
  setLoaderQueryData,
  STALE_TIME_TIER_A,
} from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";

export type PropertyTablePageLoaderType = {
  propertyList: LoaderData<APITypes.Property[]>;
  isAuthenticated: boolean;
};

export function propertyTableQueryKey(
  ownerID: string,
  ownerType: APITypes.OwnerType | "",
) {
  return ["property-table", ownerID, ownerType] as const;
}

export const propertyTableShouldRevalidate = createClientFilteredShouldRevalidate(
  (owner) => propertyTableQueryKey(owner.ownerID, owner.ownerType),
  STALE_TIME_TIER_A,
);

export async function fetchPropertyTableData(): Promise<PropertyTablePageLoaderType> {
  const user = await getReadyAuthUser();

  if (!user) {
    return {
      propertyList: {
        success: true,
        data: [],
      },
      isAuthenticated: false,
    };
  }

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) {
    return {
      propertyList: { success: true, data: [] },
      isAuthenticated: false,
    };
  }

  const { ownerID, ownerType } = authAccessInfo.data;

  const result = await apiClient.loader<PropertyTableLoaderResult>(
    "property-table",
    {
      ownerID,
      ownerType,
    },
  );

  return {
    propertyList: {
      success: result.success,
      data: result.data?.properties || [],
    },
    isAuthenticated: true,
  };
}

export async function PropertyTablePageLoader({
  request,
}: LoaderFunctionArgs): Promise<PropertyTablePageLoaderType | Response> {
  void request;
  const data = await fetchPropertyTableData();
  const auth = getCachedAuthAccessInfo();

  if (auth) {
    setLoaderQueryData(
      propertyTableQueryKey(auth.ownerID, auth.ownerType),
      data,
      STALE_TIME_TIER_A,
    );
  } else {
    setLoaderQueryData(propertyTableQueryKey("", ""), data, STALE_TIME_TIER_A);
  }

  return data;
}
